/**
 * Consentimiento Viewer Dialog
 *
 * Muestra los detalles de un consentimiento informado,
 * incluyendo el documento escaneado (PDF o imagen).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  FileCheck,
  FileX,
  Loader2,
  Download,
  ExternalLink,
  User,
  Calendar,
  FileText,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

import { useConsentimiento, descargarDocumento } from "@/hooks/use-consentimientos";

interface ConsentimientoViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consentimientoId: number | null;
}

export function ConsentimientoViewerDialog({
  open,
  onOpenChange,
  consentimientoId,
}: ConsentimientoViewerDialogProps) {
  const { t } = useTranslation();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);

  const { data: consentimiento, isLoading } = useConsentimiento(consentimientoId);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy, HH:mm", { locale: es });
    } catch {
      return dateString;
    }
  };

  /** Check if the document URL points to an image */
  const isImage = (url: string) => /\.(jpg|jpeg|png)$/i.test(url);

  // Load document blob when consentimiento changes
  useEffect(() => {
    if (consentimiento?.documento_url && open) {
      setLoadingDoc(true);
      descargarDocumento(consentimiento.documento_url)
        .then((url) => setBlobUrl(url))
        .catch(() => setBlobUrl(null))
        .finally(() => setLoadingDoc(false));
    }
    return () => {
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentimiento?.documento_url, open]);

  const handleViewInNewTab = () => {
    if (blobUrl) window.open(blobUrl, "_blank");
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const link = document.createElement("a");
    link.href = blobUrl;
    const ext =
      consentimiento?.documento_url && isImage(consentimiento.documento_url) ? "jpg" : "pdf";
    link.download = `consentimiento-${consentimientoId}.${ext}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {consentimiento?.tiene_firma ? (
              <FileCheck className="h-5 w-5 text-green-600" />
            ) : (
              <FileX className="h-5 w-5 text-muted-foreground" />
            )}
            {t("consentimientos.viewTitle", "Detalle del Consentimiento")}
          </DialogTitle>
          <DialogDescription>{consentimiento?.paciente_nombre}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : consentimiento ? (
          <div className="space-y-6">
            {/* Header Info */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{consentimiento.tipo_display}</h3>
              <Badge
                variant={consentimiento.aceptado ? "default" : "destructive"}
                className="text-sm"
              >
                {consentimiento.aceptado
                  ? t("consentimientos.accepted", "Aceptado")
                  : t("consentimientos.rejected", "Rechazado")}
              </Badge>
            </div>

            <Separator />

            {/* Signer Info */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                {t("consentimientos.signerInfo", "Información del Firmante")}
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">
                    {t("consentimientos.fields.signerName", "Nombre")}:
                  </span>
                  <p className="font-medium">{consentimiento.firmante}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {t("consentimientos.fields.signerRelation", "Relación")}:
                  </span>
                  <p className="font-medium">{consentimiento.relacion_firmante_display}</p>
                </div>
                {consentimiento.documento_firmante && (
                  <div>
                    <span className="text-muted-foreground">
                      {t("consentimientos.fields.signerDocument", "Documento")}:
                    </span>
                    <p className="font-medium">{consentimiento.documento_firmante}</p>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Date */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {t("consentimientos.signatureDetails", "Detalles de la Firma")}
              </h4>
              <div className="text-sm">
                <span className="text-muted-foreground">
                  {t("consentimientos.fields.date", "Fecha")}:
                </span>
                <p className="font-medium">{formatDate(consentimiento.fecha_firma)}</p>
              </div>
            </div>

            <Separator />

            {/* Document Display */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t("consentimientos.scannedDocument", "Documento Escaneado")}
              </h4>

              {consentimiento.documento_url ? (
                <div className="space-y-3">
                  {loadingDoc ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : blobUrl ? (
                    isImage(consentimiento.documento_url) ? (
                      <div className="border rounded-lg p-2 bg-white">
                        <img
                          src={blobUrl}
                          alt={t("consentimientos.scannedDocument", "Documento Escaneado")}
                          className="max-w-full h-auto mx-auto rounded"
                          style={{ maxHeight: "400px" }}
                        />
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <iframe
                          src={blobUrl}
                          title={t("consentimientos.scannedDocument", "Documento Escaneado")}
                          className="w-full"
                          style={{ height: "400px" }}
                        />
                      </div>
                    )
                  ) : null}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleViewInNewTab}
                      disabled={!blobUrl}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      {t("common.view", "Ver")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownload}
                      disabled={!blobUrl}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      {t("common.download", "Descargar")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg p-4 bg-muted/50 text-center text-muted-foreground">
                  <FileX className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>{t("consentimientos.noDocument", "Sin documento registrado")}</p>
                </div>
              )}
            </div>

            {/* Observations */}
            {consentimiento.observaciones && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="font-medium">
                    {t("consentimientos.fields.observations", "Observaciones")}
                  </h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {consentimiento.observaciones}
                  </p>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            {t("consentimientos.notFound", "Consentimiento no encontrado")}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
